import { type JSX, type ReactNode, useLayoutEffect } from 'react';
import { ErrorBoundary as ErrorBoundaryWrapper } from 'react-error-boundary';
import type Rollbar from 'rollbar';

import type { ErrorProps } from '../Error/Error.tsx';
import { GeneralError } from '../GeneralError/GeneralError.tsx';

type ErrorBoundaryProps = {
  children: ReactNode;
  rollbar?: Rollbar;
  redirectTo?: string;
  errorProps?: ErrorProps;
};

function logError(rollbar: Rollbar | undefined) {
  return (error: unknown, info: { componentStack?: string | null }) => {
    // biome-ignore lint/suspicious/noConsole: logging error in console
    console.error('ErrorBoundary', error, info?.componentStack);

    if (rollbar) {
      rollbar.error(error instanceof Error ? error : String(error));
    }
  };
}

const chunksLoadingErrorMessages = [
  'error loading dynamically imported module', // firefox
  'Failed to fetch dynamically imported module', // chrome
  'Importing a module script failed', // safari
  'is not a valid JavaScript MIME type', // if 404 html content is returned instead of js file
];

const isDynamicImportLoadError = (error: VitePreloadErrorEvent['payload']): boolean => {
  return error instanceof Error && chunksLoadingErrorMessages.some(msg => error.message.includes(msg));
};

export function ErrorBoundary({ children, rollbar, redirectTo, errorProps }: ErrorBoundaryProps): JSX.Element {
  const onClick = () => {
    window.location.href = redirectTo || '/';
  };

  useLayoutEffect(() => {
    const reloadedKey = 'vite:preloadError:reloaded';

    const handlePreloadError = (event: Event) => {
      const payload = 'payload' in event ? (event as { payload?: Error }).payload : undefined;

      if (!payload || !isDynamicImportLoadError(payload) || sessionStorage.getItem(reloadedKey)) {
        return;
      }

      event.preventDefault();
      sessionStorage.setItem(reloadedKey, '1');
      window.location.reload();
    };

    window.addEventListener('vite:preloadError', handlePreloadError);

    return () => {
      window.removeEventListener('vite:preloadError', handlePreloadError);
    };
  }, []);

  return (
    <ErrorBoundaryWrapper
      fallbackRender={() => <GeneralError onClick={onClick} data-test="error-refresh" {...errorProps} />}
      onError={logError(rollbar)}
    >
      {children}
    </ErrorBoundaryWrapper>
  );
}
